import { createStore, select, setProp, setProps, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { IATC } from 'src/app/entities/filters/atc/state/atc.model';
import { HttpErrorResponse } from '@angular/common/http';

export interface AtcsProps {
  searchTerm: string | null;
  letter: string | null;
  atcs: IATC[];
  selectedAtcs: IATC[];
  selectedAtcsIDs: number[];
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialProps: AtcsProps = {
  searchTerm: null,
  letter: null,
  atcs: [],
  selectedAtcs: [],
  selectedAtcsIDs: [],
  loading: false,
  error: null,
};

const STORE_NAME = 'sandoz-v2-atcs';

@Injectable({ providedIn: 'root' })
export class AtcsRepository {
  private readonly store;

  public readonly searchTerm$: Observable<string | null>;
  public readonly letter$: Observable<string | null>;
  public readonly atcs$: Observable<IATC[]>;
  public readonly selectedAtcs$: Observable<IATC[]>;
  public readonly selectedAtcsIDs$: Observable<number[]>;
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<HttpErrorResponse | null>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<AtcsProps>(initialProps),
    );

    this.searchTerm$ = this.store.pipe(
      select(state => state.searchTerm),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.letter$ = this.store.pipe(
      select(state => state.letter),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.atcs$ = this.store.pipe(
      select(state => state.atcs),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedAtcs$ = this.store.pipe(
      select(state => state.selectedAtcs),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedAtcsIDs$ = this.store.pipe(
      select(state => state.selectedAtcsIDs),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getFlattenedIds(atcs: IATC[]): number[] {
    return atcs.reduce<number[]>((acc, atc) => {
      return Array.isArray(atc.id) ? [...acc, ...atc.id] : [...acc, atc.id];
    }, []);
  }

  getStoreProp<K extends keyof AtcsProps>(key: K): AtcsProps[K] {
    const value = this.store.getValue()[key];
    if (key === 'selectedAtcsIDs' && Array.isArray(value)) {
      return value.flat() as AtcsProps[K];
    }

    return value;
  }

  setStoreProp<K extends keyof AtcsProps, V extends AtcsProps[K]>(
    key: K,
    value: V,
  ): void {
    this.store.update(setProp(key, value));
  }

  setSelectedAtcs(atcs: IATC[]): void {
    this.setStoreProp('selectedAtcs', atcs);
    this.setStoreProp('selectedAtcsIDs', this.getFlattenedIds(atcs));
  }

  setSearchTerm(searchTerm: string): void {
    this.store.update(setProps({ letter: '', searchTerm }));
  }

  setLetter(letter: string): void {
    this.store.update(setProps({ letter, searchTerm: '' }));
  }

  setLoading(loading: boolean): void {
    this.setStoreProp('loading', loading);
  }

  setError(error: HttpErrorResponse | null): void {
    this.setStoreProp('error', error);
  }

  /**
   * Save the selection of ATCs
   *
   * @param selection - selected ATCs
   */
  setSelectedATCs(selection: IATC[]): void {
    let selectedIds: number[] = [];

    selection.forEach(s => {
      const selectionValue = s.id;

      if (typeof selectionValue === 'number') {
        selectedIds.push(selectionValue);
      } else {
        selectedIds = selectedIds.concat(selectionValue);
      }
    });

    this.setStoreProp('selectedAtcs', selection);
    this.setStoreProp('selectedAtcsIDs', selectedIds);
  }

  resetStore(): void {
    this.store.reset();
  }
}
